import { FileText, Plus, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ticketsApi, knowledgeApi } from '@/api/endpoints';
import { useAuthStore } from '@/stores/authStore';
import { Button, Card } from '../components/ui';
import { TicketRow } from '../components/TicketRow';
import { adaptApiTicket, categories, sla } from '../lib/ticketMap';

// Время-зависимое приветствие по локальному часу пользователя. Границы —
// обычные бытовые: утро 5:00–11:59, день 12:00–16:59, вечер 17:00–22:59,
// иначе ночь.
function greetingByHour(hour: number): string {
  if (hour >= 5 && hour < 12) return 'Доброе утро';
  if (hour >= 12 && hour < 17) return 'Добрый день';
  if (hour >= 17 && hour < 23) return 'Добрый вечер';
  return 'Доброй ночи';
}

export function HomeView() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const displayName =
    (user?.contactPerson || '').trim().split(/\s+/)[0] ||
    (user?.companyName || '').trim() ||
    '';
  const greeting = greetingByHour(new Date().getHours());
  const title = displayName ? `${greeting}, ${displayName}` : greeting;

  const { data: ticketsPage } = useQuery({
    queryKey: ['home-tickets'],
    queryFn: () => ticketsApi.list({ limit: 5, sortBy: 'updatedAt', sortOrder: 'desc' }),
    staleTime: 30_000,
  });
  const { data: kbPage } = useQuery({
    queryKey: ['home-kb-popular'],
    queryFn: () => knowledgeApi.getArticles({ limit: 5 }),
    staleTime: 60_000,
  });

  const tickets = (ticketsPage?.data || []).map(adaptApiTicket);
  const visibleTickets = tickets.slice(0, 3);
  const popularArticles = kbPage?.data || [];

  const goCreate = (preset?: string) => {
    if (preset) navigate(`/tickets/new?type=${encodeURIComponent(preset)}`);
    else navigate('/tickets/new');
  };

  return (
    <div className="view active">
      <section className="hero">
        <div className="hero-globe" />
        <div className="watermark">HELP</div>
        <span className="hero-tag">
          <i />
          CLUB:CORE / CLIENT SUPPORT
        </span>
        <h1>{title}</h1>
        <p>Опишите проблему с софтом или найдите готовый ответ в базе знаний. Большинство вопросов решается за пару минут.</p>
        <div className="hero-search">
          <div className="search-field">
            <Search size={19} />
            <input
              placeholder="Например: «не запускается клиент», «ошибка лицензии»..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const v = (e.target as HTMLInputElement).value.trim();
                  if (v) navigate(`/knowledge?q=${encodeURIComponent(v)}`);
                }
              }}
            />
          </div>
          <Button className="btn-primary btn-lg" aria-label="Открыть мастер обращения" onClick={() => goCreate()}>
            <Plus size={18} />
            Создать обращение
          </Button>
        </div>
      </section>

      <div className="section-head">
        <h3>С чем возникла проблема?</h3>
        <span>выберите — форма заполнится автоматически</span>
      </div>
      <div className="category-grid">
        {categories.slice(0, 4).map((category) => {
          const Icon = category.icon;
          return (
            <button type="button" className="category-card" key={category.id} onClick={() => goCreate(category.id)}>
              <span className="mini-icon">
                <Icon size={22} />
              </span>
              <h4>{category.label}</h4>
              <p>{category.description}</p>
            </button>
          );
        })}
      </div>

      <div className="dashboard-grid">
        <Card className="home-ticket-card">
          <div className="card-head">
            <h3>Мои обращения</h3>
            <div className="filters">
              <button className="chip active" type="button">Все</button>
              <button className="chip" type="button" onClick={() => navigate('/tickets')}>Открытые</button>
              <button className="chip" type="button" onClick={() => navigate('/tickets')}>Решённые</button>
            </div>
          </div>
          <div className="ticket-list">
            {visibleTickets.length ? (
              visibleTickets.map((ticket) => <TicketRow ticket={ticket} key={ticket.id} />)
            ) : (
              <div className="empty">Пока обращений нет — создайте первое кнопкой «Создать обращение» сверху.</div>
            )}
          </div>
        </Card>

        <aside className="aside-stack">
          <Card>
            <div className="card-head">
              <h3>Популярное в базе знаний</h3>
            </div>
            <div className="kb-list">
              {popularArticles.length ? popularArticles.map((article: any) => (
                <a
                  className="kb-item"
                  key={article.id}
                  onClick={() => navigate(`/knowledge/${article.slug || article.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="mini-icon">
                    <FileText size={17} />
                  </span>
                  <span>
                    <strong>{article.title}</strong>
                    <em>
                      {article.category?.name || 'Без категории'} · {article.viewCount ?? 0} просмотров
                    </em>
                  </span>
                </a>
              )) : (
                <div className="empty">База знаний пока пуста.</div>
              )}
            </div>
          </Card>
          <Card>
            <div className="card-head">
              <h3>Время ответа (SLA)</h3>
            </div>
            {(Object.keys(sla) as Array<keyof typeof sla>).map((key) => (
              <div className="sla-row" key={key}>
                <span>
                  <i className={`dot sev-dot-${key.toLowerCase()}`} />
                  {key}
                </span>
                <strong>{sla[key]}</strong>
              </div>
            ))}
          </Card>
        </aside>
      </div>
    </div>
  );
}
